import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Paper,
  Skeleton,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Reply as ReplyIcon,
  Forward as ForwardIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { fetchUsersByIds, deleteDraftMessage, moveThreadToTrash } from '../../services/messagingService';

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
};

const MessageDetail = ({ thread, messages = [], loading, onThreadDeleted, onReply, onForward, onEditDraft }) => {
  const [participantsInfo, setParticipantsInfo] = useState([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [starred, setStarred] = useState(false);

  const latestMsg = messages[messages.length - 1];
  const isDraft = latestMsg?.type === 'draft';

  useEffect(() => {
    let ignore = false;
    async function fetchParticipants() {
      if (isDraft) {
        if (!latestMsg?.recipients || latestMsg.recipients.length === 0) {
          setParticipantsInfo([]);
          return;
        }
        setParticipantsLoading(true);
        try {
          const response = await fetchUsersByIds(latestMsg.recipients);
          if (!ignore) {
            const users = response?.users || [];
            setParticipantsInfo(Array.isArray(users) ? users : []);
            setParticipantsLoading(false);
          }
        } catch (error) {
          console.error('Error fetching draft recipients:', error);
          if (!ignore) {
            setParticipantsInfo([]);
            setParticipantsLoading(false);
          }
        }
      } else {
        if (!thread?.participants || thread.participants.length === 0) {
          setParticipantsInfo([]);
          return;
        }
        setParticipantsLoading(true);
        try {
          const response = await fetchUsersByIds(thread.participants);
          if (!ignore) {
            const users = response?.users || [];
            setParticipantsInfo(Array.isArray(users) ? users : []);
            setParticipantsLoading(false);
          }
        } catch (error) {
          console.error('Error fetching thread participants:', error);
          if (!ignore) {
            setParticipantsInfo([]);
            setParticipantsLoading(false);
          }
        }
      }
    }
    fetchParticipants();
    return () => { ignore = true; };
  }, [thread, latestMsg, isDraft]);

  if (!thread) {
    return (
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        textAlign: 'center',
        color: 'text.secondary',
      }}>
        <Box 
          component="img"
          src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 24 24' fill='none' stroke='%23cbd5e1' stroke-width='1' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z'%3E%3C/path%3E%3Cpolyline points='22,6 12,13 2,6'%3E%3C/polyline%3E%3C/svg%3E"
          alt="No message selected"
          sx={{ width: 100, height: 100, mb: 2, opacity: 0.6 }}
        />
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
          Select a message
        </Typography>
        <Typography variant="body2" color="text.disabled">
          Choose a message from the list to view its contents
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ flex: 1, p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Skeleton variant="circular" width={52} height={52} sx={{ mr: 2 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="40%" height={28} />
            <Skeleton variant="text" width="30%" height={20} />
          </Box>
        </Box>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (!latestMsg) {
    return (
      <Box sx={{ flex: 1, p: 3, color: 'text.secondary' }}>
        <Typography>No messages in this thread.</Typography>
      </Box>
    );
  }

  const department = latestMsg.department || '';
  const avatar = latestMsg.avatarUrl || null;
  const initials = getInitials(latestMsg.senderName);

  const recipientIds = isDraft 
    ? (latestMsg.recipients || [])
    : (thread.participants?.filter(p => p !== latestMsg.sentBy) || []);
  const recipientNames = (Array.isArray(participantsInfo) ? participantsInfo : [])
    .filter(u => recipientIds.includes(u.id))
    .map(u => u.displayName || u.email || u.id);

  const handleDelete = async () => {
    if (!thread?.id) return;
    setDeleting(true);
    
    try {
      if (isDraft) {
        await deleteDraftMessage(thread.id);
      } else {
        await moveThreadToTrash(thread.id);
      }
      
      if (onThreadDeleted) onThreadDeleted();
    } catch (error) {
      console.error('Error deleting/moving to trash:', error);
      alert('Failed to delete/move to trash');
    } finally {
      setDeleting(false);
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp?.seconds) return '';
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleString([], { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      hour: '2-digit', 
      minute: '2-digit',
    });
  };

  return (
    <Box sx={{ 
      flex: 1, 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <Paper 
        elevation={0}
        sx={{ 
          p: { xs: 2, md: 3 }, 
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {/* Avatar */}
          <Avatar
            src={avatar}
            sx={{
              width: 52,
              height: 52,
              bgcolor: isDraft ? '#fef3c7' : 'primary.100',
              color: isDraft ? '#92400e' : 'primary.700',
              fontWeight: 700,
              fontSize: '1.1rem',
            }}
          >
            {!avatar && initials}
          </Avatar>

          {/* Sender Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                {latestMsg.senderName || 'Unknown'}
              </Typography>
              {isDraft && (
                <Chip 
                  label="Draft" 
                  size="small" 
                  sx={{ 
                    bgcolor: '#fef3c7', 
                    color: '#92400e',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                  }} 
                />
              )}
            </Box>
            {department && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                {department}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  To: {participantsLoading ? 'Loading...' : recipientNames.length > 0 ? recipientNames.join(', ') : 'Unknown'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TimeIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  {formatDateTime(latestMsg.sentAt)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          mt: 2,
          flexWrap: 'wrap',
        }}>
          {isDraft ? (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => onEditDraft && onEditDraft(latestMsg)}
              sx={{
                bgcolor: 'primary.main',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              Edit Draft
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                startIcon={<ReplyIcon />}
                onClick={() => onReply && onReply(latestMsg)}
                sx={{
                  bgcolor: 'primary.main',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                Reply
              </Button>
              <Button
                variant="outlined"
                startIcon={<ForwardIcon />}
                onClick={() => onForward && onForward(latestMsg)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: 'divider',
                  color: 'text.primary',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
                }}
              >
                Forward
              </Button>
            </>
          )}
          <Tooltip title={starred ? 'Unstar' : 'Star'}>
            <IconButton 
              onClick={() => setStarred(!starred)}
              sx={{ 
                color: starred ? '#f59e0b' : 'text.secondary',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {starred ? <StarIcon /> : <StarBorderIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title={isDraft ? 'Delete Draft' : 'Move to Trash'}>
            <IconButton
              onClick={handleDelete}
              disabled={deleting}
              sx={{
                color: 'error.main',
                '&:hover': { bgcolor: 'error.50' },
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Message Content */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        p: { xs: 2, md: 3 },
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-track': {
          bgcolor: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: 'rgba(0,0,0,0.15)',
          borderRadius: 3,
          '&:hover': {
            bgcolor: 'rgba(0,0,0,0.25)',
          },
        },
      }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'white',
            maxWidth: 900,
          }}
        >
          {/* Subject */}
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600, 
              mb: 2,
              pb: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              color: 'text.primary',
            }}
          >
            {thread.subject || '(No subject)'}
          </Typography>

          {/* Message Body */}
          <Typography 
            component="div"
            sx={{ 
              fontSize: '0.95rem',
              lineHeight: 1.7,
              color: 'text.primary',
              '& p': { mb: 1.5 },
              '& a': { color: 'primary.main' },
            }}
            dangerouslySetInnerHTML={{ __html: latestMsg.content || '<p>No content</p>' }}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default MessageDetail;
